import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { getObstacleIcon, getArpIcon } from '../../utils/markers';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { addHullPolygonsToMap, getObstaclesToRender } from '../../utils/hullMapLayer';
import { T } from '../../assets/theme';
import { useSurfaceStyles } from '../../assets/SurfaceStyleContext';
import { DraggableModal, ResizeHandle } from '../../components/UI';
import { useResizableHeight } from '../../utils/useResizableHeight';
import MapMeasureTool from '../../components/MapMeasureTool';
import Cesium3DPreviewModal from '../../components/Cesium3DPreviewModal';
import MapCoordinateViewer from '../../components/MapCoordinateViewer';
import { useShield } from '../../utils/useShield';
import ShieldControl from '../../components/ShieldControl';

const exportToCsv = (headers, rows, filename) => {
    const csvContent = [
        headers.join(","),
        ...rows.map(row => row.map(val => {
            const cleanVal = String(val ?? '').replace(/—/g, '-');
            return `"${cleanVal.replace(/"/g, '""')}"`;
        }).join(","))
    ].join("\n");
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
};

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
    iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
    shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
});

const TILE_LAYERS = {
    dark: { label: '🌙 Dark', url: 'https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png', attribution: '© CARTO' },
    street: { label: '🗺 Street', url: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', attribution: '© OSM' },
    satellite: { label: '🛰 Satellite', url: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}', attribution: '© Esri' },
};

const DEG = Math.PI / 180;

function StatusBadge({ s }) {
    const c = s === 'penetrated' || s === 'CRM assessment required' ? T.danger : s === 'clear' || s === 'No impact' ? T.safe : T.muted;
    return <span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: 10, fontSize: 10, fontWeight: 700, color: '#fff', background: c + '33', border: `1px solid ${c}55` }}>{s?.toUpperCase()}</span>;
}

// ── Helpers ─────────────────────────────────────────────────────────────────
function computeHeading(lat1, lon1, lat2, lon2) {
    const dLon = (lon2 - lon1) * DEG;
    const y = Math.sin(dLon) * Math.cos(lat2 * DEG);
    const x = Math.cos(lat1 * DEG) * Math.sin(lat2 * DEG) -
              Math.sin(lat1 * DEG) * Math.cos(lat2 * DEG) * Math.cos(dLon);
    return (Math.atan2(y, x) * 180 / Math.PI + 360) % 360;
}

function localToLatLng(thr_lat, thr_lon, heading_deg, x, y) {
    const R = 6371000;
    // approach_heading = heading + 180
    const approachHdg = (heading_deg + 180) % 360;
    const angle = Math.atan2(y, x); // angle relative to approach direction
    const dist = Math.sqrt(x * x + y * y);
    const bearing = (approachHdg + angle * 180 / Math.PI + 360) % 360;

    const lat1 = thr_lat * DEG;
    const lon1 = thr_lon * DEG;
    const brRad = bearing * DEG;
    const angDist = dist / R;

    const lat2 = Math.asin(Math.sin(lat1) * Math.cos(angDist) +
                           Math.cos(lat1) * Math.sin(angDist) * Math.cos(brRad));
    const lon2 = lon1 + Math.atan2(Math.sin(brRad) * Math.sin(angDist) * Math.cos(lat1),
                                    Math.cos(angDist) - Math.sin(lat1) * Math.sin(lat2));

    return [lat2 * 180 / Math.PI, lon2 * 180 / Math.PI];
}

// ── Surface colors ──────────────────────────────────────────────────────────
const SURFACE_COLORS = {
    Ground: '#FF6B6B',
    W: '#4ECDC4',
    X: '#45B7D1',
    X_left: '#45B7D1',
    X_right: '#45B7D1',
    Y: '#F4D03F',
    Y_left: '#F4D03F',
    Y_right: '#F4D03F',
    Z: '#9B59B6',
};

const EMPTY_OBJ = {};
const EMPTY_ARR = [];

export default function PansOpsPage({ project, arps, obstacles, loading, pansState, setPansState, showObstacles = true, showObsLabels, showArpLabels, onObstacleContextMenu, onMapReady, onAerodromeContextMenu }) {
    const mapRef = useRef(null), mapInst = useRef(null), tileRef = useRef(null), layersRef = useRef([]);
    // Step 2 reference set — this project's own obstacles that actually
    // penetrate a PANS-OPS surface, not just its tallest point overall.
    const ilsResultsForShield = pansState?.ilsResults || EMPTY_OBJ;
    const penetratingObstacleIds = useMemo(() => {
        const ids = [];
        Object.values(ilsResultsForShield).forEach(entry => {
            (entry?.procedures || []).forEach(p => {
                (p.results || []).forEach(r => { if (r.penetration > 0) ids.push(r.obstacle_id); });
            });
        });
        return ids;
    }, [ilsResultsForShield]);
    const { shieldOn, toggleShield, shieldData, shieldLoading, shieldError, projectMaxTotalElev, projectPenetratingMaxTotalElev, radiusKm, setRadiusKm, exportCsv } = useShield(mapInst, obstacles, penetratingObstacleIds);
    const { getStyle } = useSurfaceStyles();

    // Ephemeral state
    const [mapMode, setMapMode] = useState('street');
    const [selObstId, setSelObstId] = useState(null);
    const [mapState, setMapState] = useState(null);
    const [loadingIls, setLoadingIls] = useState({});
    const [selectedProc, setSelectedProc] = useState(null); // { icao, procIdx }
    const [resultsCollapsed, setResultsCollapsed] = useState(false);
    const [resultsHeight, startResultsResize] = useResizableHeight(245, { min: 120, max: 700 });
    const [resultsPoppedOut, setResultsPoppedOut] = useState(false);

    useEffect(() => {
        if (mapInst.current) {
            setTimeout(() => { mapInst.current?.invalidateSize(); }, 200);
        }
    }, [resultsCollapsed, resultsPoppedOut]);

    // Bind state externally
    const visible = pansState?.visible || EMPTY_OBJ;
    const setVisible = useCallback((val) => setPansState(prev => ({ ...prev, visible: typeof val === 'function' ? val(prev.visible || EMPTY_OBJ) : val })), [setPansState]);

    const ilsIcaos = pansState?.ilsIcaos || EMPTY_ARR;
    const setIlsIcaos = useCallback((val) => setPansState(prev => ({ ...prev, ilsIcaos: typeof val === 'function' ? val(prev.ilsIcaos || EMPTY_ARR) : val })), [setPansState]);

    const ilsData = pansState?.ilsData || EMPTY_OBJ;
    const setIlsData = useCallback((val) => setPansState(prev => ({ ...prev, ilsData: typeof val === 'function' ? val(prev.ilsData || EMPTY_OBJ) : val })), [setPansState]);

    const ilsResults = pansState?.ilsResults || EMPTY_OBJ;
    const setIlsResults = useCallback((val) => setPansState(prev => ({ ...prev, ilsResults: typeof val === 'function' ? val(prev.ilsResults || EMPTY_OBJ) : val })), [setPansState]);

    const visIls = pansState?.visIls || EMPTY_OBJ;
    const setVisIls = useCallback((val) => setPansState(prev => ({ ...prev, visIls: typeof val === 'function' ? val(prev.visIls || EMPTY_OBJ) : val })), [setPansState]);

    const capturedIls = pansState?.capturedIls || EMPTY_OBJ;
    const setCapturedIls = useCallback((val) => setPansState(prev => ({ ...prev, capturedIls: typeof val === 'function' ? val(prev.capturedIls || EMPTY_OBJ) : val })), [setPansState]);

    const obsWithCoords = obstacles.filter(o => o.latitude && o.longitude);
    const selectedObs = obsWithCoords.find(o => o.id === selObstId) || obsWithCoords[0];

    // Fetch ILS aerodrome list
    useEffect(() => {
        if (ilsIcaos.length > 0) return;
        const fetchIlsAerodromes = async () => {
            try {
                const { getPansAerodromes } = await import('../../api/client');
                const res = await getPansAerodromes();
                setIlsIcaos(res.data || []);
            } catch (err) {
                console.error('Failed to fetch ILS aerodromes:', err);
            }
        };
        fetchIlsAerodromes();
    }, [ilsIcaos.length]); // eslint-disable-line

    const validArps = React.useMemo(() => arps.filter(a => ilsIcaos.includes(a.icao?.toUpperCase())), [arps, ilsIcaos]);

    // Init visibility
    useEffect(() => {
        if (!validArps.length) return;
        setVisible(prev => {
            if (Object.keys(prev || {}).length > 0) return prev; // Preserve toggles across unmounts
            const vis = {};
            validArps.forEach(a => { vis[a.icao] = true; });
            return vis;
        });
    }, [validArps, setVisible]);

    // Load ILS data for an aerodrome
    const loadIlsData = useCallback(async (icao) => {
        if (ilsData[icao]) return;
        setLoadingIls(prev => ({ ...prev, [icao]: true }));
        try {
            const { getIlsProcedures } = await import('../../api/client');
            const res = await getIlsProcedures(icao);
            setIlsData(prev => ({ ...prev, [icao]: res.data }));
        } catch (err) {
            console.error(`Failed to load ILS for ${icao}:`, err);
        } finally {
            setLoadingIls(prev => ({ ...prev, [icao]: false }));
        }
    }, [ilsData]);

    // Map init
    useEffect(() => {
        if (!mapRef.current || mapInst.current) return;
        mapInst.current = L.map(mapRef.current, {
            center: [-28.5, 25.0],
            zoom: 6,
            zoomDelta: 0.7,
            zoomSnap: 0.7
        });
        tileRef.current = L.tileLayer(TILE_LAYERS[mapMode].url, { attribution: TILE_LAYERS[mapMode].attribution, maxZoom: 24, maxNativeZoom: 19 }).addTo(mapInst.current);
        const ro = new ResizeObserver(() => { mapInst.current?.invalidateSize(); });
        ro.observe(mapRef.current);
        setMapState(mapInst.current);
        onMapReady?.(mapInst.current);
        return () => { ro.disconnect(); mapInst.current?.remove(); mapInst.current = null; tileRef.current = null; setMapState(null); onMapReady?.(null); };
    }, []);

    // Swap tile layer when map mode changes
    useEffect(() => {
        if (!mapInst.current) return;
        if (tileRef.current) { mapInst.current.removeLayer(tileRef.current); }
        const layer = TILE_LAYERS[mapMode];
        tileRef.current = L.tileLayer(layer.url, { attribution: layer.attribution, maxZoom: 24, maxNativeZoom: 19 }).addTo(mapInst.current);
    }, [mapMode]);

    // Draw map layers
    useEffect(() => {
        console.log('[PansOpsPage] Drawing useEffect triggered! Map present:', !!mapInst.current, 'loading:', loading, 'ilsResults keys:', Object.keys(ilsResults || {}));
        if (!mapInst.current || loading) return;
        layersRef.current.forEach(l => mapInst.current.removeLayer(l));
        layersRef.current = [];
        const bounds = [];

        validArps.forEach(arp => {
            if (!arp.lat || !arp.lon) return;
            const ll = [arp.lat, arp.lon]; bounds.push(ll);
            const isOn = visible[arp.icao] !== false;

            const m = L.marker(ll, { icon: getArpIcon(L, arp, showArpLabels), opacity: isOn ? 1 : 0.3 })
                .bindPopup(`<b>✈ ${arp.icao}</b><br/>${arp.name}`);
            if (onAerodromeContextMenu) {
                m.on('contextmenu', e => {
                    e.originalEvent.preventDefault();
                    onAerodromeContextMenu(arp, e.originalEvent.clientX, e.originalEvent.clientY);
                });
            }
            m.addTo(mapInst.current); layersRef.current.push(m);
            if (!isOn) return;

            // Draw ILS surfaces if loaded
            const ad = ilsData[arp.icao];
            if (ad && ad.procedures) {
                ad.procedures.forEach((proc) => {
                    if (!proc.template_coords || proc.template_coords.length === 0) return;

                    // Find approach threshold
                    const thrMatch = (ad.thresholds || []).find(t => {
                        const rawRwy = (t.rwy && t.rwy !== 'NULL') ? t.rwy : t.threshold;
                        const tRwy = String(rawRwy || '').replace(/^0+/, '').toUpperCase();
                        const pRwy = String(proc.runway || '').replace(/^0+/, '').toUpperCase();
                        return tRwy === pRwy;
                    });
                    if (!thrMatch) return;

                    const procKey = `${arp.icao}_${proc.runway}_${proc.ils_category}`;
                    if (visIls[procKey] === false) return; // Default to visible if undefined, or hide if explicitly false

                    // Find departure threshold (paired)
                    const thrid = thrMatch.thrid;
                    const numStr = thrid.replace(/\D/g, '');
                    const num = parseInt(numStr, 10);
                    const isOdd = num % 2 === 1;
                    const pairNum = isOdd ? num + 1 : num - 1;
                    const prefix = thrid.replace(/\d+$/, '');
                    const pairId = prefix + String(pairNum).padStart(numStr.length, '0');
                    const depThr = (ad.thresholds || []).find(t => t.thrid === pairId);
                    if (!depThr) return;

                    const thr_lat = parseFloat(thrMatch.thr_lat);
                    const thr_lon = parseFloat(thrMatch.thr_lon);
                    const dep_lat = parseFloat(depThr.thr_lat);
                    const dep_lon = parseFloat(depThr.thr_lon);
                    const heading = computeHeading(thr_lat, thr_lon, dep_lat, dep_lon);

                    const c = proc.template_coords[0];
                    if (!c) return;

                    const px = (lx, ly) => localToLatLng(thr_lat, thr_lon, heading, lx, ly);

                    // Helper to get coordinate tuple [x, y]
                    const pt = (coords, name, mirror_y = false) => {
                        let x, y;
                        if (name === "C") { x = coords.cx; y = coords.cy;
                        } else if (name === "D") { x = coords.dx; y = coords.dy;
                        } else if (name === "E") { x = coords.ex; y = coords.ey;
                        } else if (name === "Cpp") { x = coords.cpp_x ?? coords.cx; y = coords.cpp_y ?? coords.cy; // C"
                        } else if (name === "Dpp") { x = coords.dpp_x ?? coords.dx; y = coords.dpp_y ?? coords.dy; // D"
                        } else if (name === "Epp") { x = coords.epp_x ?? coords.ex; y = coords.epp_y ?? coords.ey; // E"
                        } else if (name === "Cppp") { x = coords.cppp_x ?? coords.cpp_x ?? coords.cx; y = coords.cppp_y ?? coords.cpp_y ?? coords.cy; // C""
                        } else { x = 0; y = 0; }
                        return [parseFloat(x || 0), parseFloat(mirror_y ? -(y || 0) : (y || 0))];
                    };

                    const isCatII = (proc.ils_category || '').includes('II');
                    const polygons = {};

                    if (isCatII) {
                        polygons.W = [pt(c, 'Cppp'), pt(c, 'C'), pt(c, 'C', true), pt(c, 'Cppp', true)];
                        polygons.X_left = [pt(c, 'Cpp'), pt(c, 'Dpp'), pt(c, 'D'), pt(c, 'C'), pt(c, 'Cppp')];
                        polygons.X_right = [pt(c, 'Cpp', true), pt(c, 'Dpp', true), pt(c, 'D', true), pt(c, 'C', true), pt(c, 'Cppp', true)];
                    } else {
                        polygons.W = [pt(c, 'Cpp'), pt(c, 'C'), pt(c, 'C', true), pt(c, 'Cpp', true)];
                        polygons.X_left = [pt(c, 'Cpp'), pt(c, 'Dpp'), pt(c, 'D'), pt(c, 'C')];
                        polygons.X_right = [pt(c, 'Cpp', true), pt(c, 'Dpp', true), pt(c, 'D', true), pt(c, 'C', true)];
                    }
                    polygons.Y_left = [pt(c, 'Dpp'), pt(c, 'Epp'), pt(c, 'E'), pt(c, 'D')];
                    polygons.Y_right = [pt(c, 'Dpp', true), pt(c, 'Epp', true), pt(c, 'E', true), pt(c, 'D', true)];
                    polygons.Z = [pt(c, 'E'), pt(c, 'Epp'), pt(c, 'Epp', true), pt(c, 'E', true)];
                    polygons.Ground = [pt(c, 'C'), pt(c, 'D'), pt(c, 'E'), pt(c, 'E', true), pt(c, 'D', true), pt(c, 'C', true)];

                    const drawPolygon = (pts, name, color, opacity = 0.45) => {
                        if (!pts || pts.length < 3) return;
                        const style = { color, weight: 1.5, fillColor: color, fillOpacity: opacity };
                        const latlngs = pts.map(p => px(p[0], p[1]));
                        const poly = L.polygon(latlngs, style).addTo(mapInst.current);
                        poly.bindTooltip(`Surface ${name}`);
                        layersRef.current.push(poly);
                    };

                    const wStyle = getStyle('pans_ops.w');
                    const xStyle = getStyle('pans_ops.x');
                    const yStyle = getStyle('pans_ops.y');
                    const zStyle = getStyle('pans_ops.z');
                    drawPolygon(polygons.W, 'W', wStyle.color, wStyle.opacity);
                    drawPolygon(polygons.X_left, 'X (Left)', xStyle.color, xStyle.opacity);
                    drawPolygon(polygons.X_right, 'X (Right)', xStyle.color, xStyle.opacity);
                    drawPolygon(polygons.Y_left, 'Y (Left)', yStyle.color, yStyle.opacity);
                    drawPolygon(polygons.Y_right, 'Y (Right)', yStyle.color, yStyle.opacity);
                    drawPolygon(polygons.Z, 'Z', zStyle.color, zStyle.opacity);

                    // 5. Footprint Outlines (Polylines only, no fill)
                    const basePoints = polygons.Ground.map(([lx, ly]) => px(lx, ly));
                    const highPoints = [pt(c, 'Epp'), pt(c, 'Dpp'), pt(c, 'Cpp'), pt(c, 'Cpp', true), pt(c, 'Dpp', true), pt(c, 'Epp', true), pt(c, 'Epp')].map(([lx, ly]) => px(lx, ly));
                    
                    const lb = L.polyline(basePoints, { color: getStyle('pans_ops.ground').color, weight: 1.5, dashArray: '5,5' }).addTo(mapInst.current);
                    const lh = L.polyline(highPoints, { color: '#fff', weight: 1, dashArray: '2,2' }).addTo(mapInst.current);
                    layersRef.current.push(lb, lh);

                    // Draw threshold marker
                    const thrMarker = L.circleMarker([thr_lat, thr_lon], {
                        radius: 5, color: '#FFD700', fillColor: '#FFD700', fillOpacity: 0.9, weight: 2
                    }).bindPopup(`<b>THR RWY ${proc.runway}</b><br/>Elev: ${thrMatch.elev_m} m`);
                    thrMarker.addTo(mapInst.current);
                    layersRef.current.push(thrMarker);
                });
            }
        });

        getObstaclesToRender(obsWithCoords, project?.id, showObstacles).forEach(o => {
            const ll = [o.latitude, o.longitude]; bounds.push(ll);
            const isSel = o.id === selectedObs?.id;
            // The penetrating status logic requires looking into full assessments. Leaving this unchanged for now.
            let isPenetrating = false;
            let isAssessed = false;
            let maxPen = -Infinity;
            let foundAny = false;
            let worstRes = null;
            Object.keys(ilsResults).forEach(icao => {
                isAssessed = true;
                const procs = ilsResults[icao]?.procedures || [];
                for(const p of procs) {
                    const hit = p.results?.find(r => r.obstacle_id === o.id);
                    if (hit) {
                        foundAny = true;
                        if (hit.penetration > maxPen) {
                            maxPen = hit.penetration;
                            worstRes = hit;
                        }
                        if (hit.penetration > 0) isPenetrating = true;
                    }
                }
            });

            let penetrationStr = '—';
            let surfaceStr = '—';
            if (foundAny) {
                if (maxPen > 0) {
                    penetrationStr = `${maxPen.toFixed(2)} m`;
                } else {
                    penetrationStr = `${maxPen.toFixed(2)} m (Clear)`;
                }
                if (worstRes) {
                    surfaceStr = worstRes.surface_name || worstRes.surface || '—';
                }
            }

            const obsIconUrl = isPenetrating ? '/symbols/obstacle_red.svg' : '/symbols/obstacle_green.svg';
            const sz = isSel ? 80 : 65; const h = Math.round(sz / 2);
            const icon = getObstacleIcon(L, o, isAssessed ? (isPenetrating ? 'penetrated' : 'clear') : undefined, showObsLabels);
            const m = L.marker(ll, { icon, zIndexOffset: isSel ? 100 : 0 })
                .bindPopup(`<b>${o.name}</b><br/>ID: ${o.id}<br/>ELEVATION : ${o.elevation_m ?? '—'} m<br/>SURFACE: ${surfaceStr}<br/>PENETRATION: ${penetrationStr}`)
                .on('click', () => setSelObstId(o.id));
            if (onObstacleContextMenu) {
                m.on('contextmenu', e => {
                    e.originalEvent.preventDefault();
                    onObstacleContextMenu(o, e.originalEvent.clientX, e.originalEvent.clientY);
                });
            }
            m.addTo(mapInst.current); layersRef.current.push(m);
        });
        addHullPolygonsToMap(mapInst.current, obstacles, project?.id, layersRef);

        if (bounds.length) mapInst.current.fitBounds(L.latLngBounds(bounds), { padding: [60, 60], maxZoom: 12 });
    }, [validArps, obstacles, loading, ilsData, visIls, visible, showObstacles, showObsLabels, showArpLabels, selObstId, ilsResults, onObstacleContextMenu, onAerodromeContextMenu, getStyle]);

    // Build current procedure's matched result group (results + threshold elevation) from assess-all
    const getCurrentMatchProc = () => {
        if (!selectedProc) return null;
        const ad = ilsData[selectedProc.icao];
        if (!ad || !ad.procedures) return null;
        const proc = ad.procedures[selectedProc.procIdx];
        if (!proc) return null;

        // Get assessment results from the ILS engine
        const results = ilsResults[selectedProc.icao];
        if (!results) return null;

        return (results.procedures || []).find(p =>
            p.runway === proc.runway && p.ils_category === proc.ils_category
        ) || null;
    };

    const getCurrentResults = () => getCurrentMatchProc()?.results || [];

    const runIlsAssessment = useCallback(async (icao) => {
        try {
            const api = (await import('../../api/client')).default;
            const res = await api.post(`/pans/ils-assess-full/${icao}`, { obstacles: obsWithCoords.map(o => ({
                id: o.id, name: o.name, latitude: o.latitude, longitude: o.longitude, elevation_m: o.elevation_m || 0, h_tolerance: o.h_tolerance || 0, v_tolerance: o.v_tolerance || 0
            })) });
            setPansState(prev => ({ ...prev, ilsResults: { ...prev.ilsResults, [icao]: res.data } }));
        } catch (err) {
            // Fallback: try the simple GET endpoint
            try {
                const { assessIls } = await import('../../api/client');
                const res = await assessIls(icao);
                setPansState(prev => ({ ...prev, ilsResults: { ...prev.ilsResults, [icao]: res.data } }));
            } catch (err2) {
                console.error(`Failed to assess ILS for ${icao}:`, err2);
            }
        }
    }, [obsWithCoords, setPansState]);

    const captureIlsScreenshot = useCallback(async (icao, runway, category) => {
        if (!mapRef.current) return;
        const procKey = `${icao}_${runway}_${category}`;
        try {
            const container = mapRef.current;
            const containerRect = container.getBoundingClientRect();
            const w = Math.round(containerRect.width);
            const h = Math.round(containerRect.height);

            const canvas = document.createElement('canvas');
            canvas.width = w;
            canvas.height = h;
            const ctx = canvas.getContext('2d');
            ctx.fillStyle = '#07111e';
            ctx.fillRect(0, 0, w, h);

            // Draw base map tiles
            const tileEls = [...container.querySelectorAll('.leaflet-tile:not(.leaflet-tile-loading)')];
            await Promise.all(tileEls.map(async tile => {
                if (!tile.src) return;
                const tRect = tile.getBoundingClientRect();
                const tx = Math.floor(tRect.left - containerRect.left);
                const ty = Math.floor(tRect.top - containerRect.top);
                const tw = Math.ceil(tRect.width) + 1;
                const th = Math.ceil(tRect.height) + 1;
                try {
                    const resp = await fetch(tile.src, { mode: 'cors' });
                    if (!resp.ok) return;
                    const blob = await resp.blob();
                    const url = URL.createObjectURL(blob);
                    await new Promise(resolve => {
                        const img = new Image();
                        img.onload = () => { ctx.drawImage(img, tx, ty, tw, th); URL.revokeObjectURL(url); resolve(); };
                        img.onerror = () => { URL.revokeObjectURL(url); resolve(); };
                        img.src = url;
                    });
                } catch { }
            }));

            // Draw SVG overlay
            const svgEl = container.querySelector('.leaflet-overlay-pane svg');
            if (svgEl) {
                const svgRect = svgEl.getBoundingClientRect();
                const sx = Math.round(svgRect.left - containerRect.left);
                const sy = Math.round(svgRect.top - containerRect.top);
                const clone = svgEl.cloneNode(true);
                clone.style.transform = '';
                clone.style.left = '0';
                clone.style.top = '0';
                const svgStr = new XMLSerializer().serializeToString(clone);
                const blob = new Blob([svgStr], { type: 'image/svg+xml;charset=utf-8' });
                const url = URL.createObjectURL(blob);
                await new Promise(resolve => {
                    const img = new Image();
                    img.onload = () => { ctx.drawImage(img, sx, sy, Math.round(svgRect.width), Math.round(svgRect.height)); URL.revokeObjectURL(url); resolve(); };
                    img.onerror = () => { URL.revokeObjectURL(url); resolve(); };
                    img.src = url;
                });
            }

            // Draw markers
            const markerEls = [...container.querySelectorAll('.leaflet-marker-icon')];
            for (const marker of markerEls) {
                const mRect = marker.getBoundingClientRect();
                const x = Math.round(mRect.left - containerRect.left);
                const y = Math.round(mRect.top - containerRect.top);
                const img = marker.querySelector('img');
                if (img && img.complete && img.naturalWidth) {
                    try { ctx.drawImage(img, x, y, Math.round(mRect.width), Math.round(mRect.height)); } catch {}
                }
            }

            const dataUrl = canvas.toDataURL('image/jpeg', 0.88);
            const api = (await import('../../api/client')).default;
            await api.post(`/pans/ils-surfaces/${icao}_${runway}_${category}/screenshot`, { image: dataUrl, project_id: project?.id });
            setCapturedIls(prev => ({ ...prev, [procKey]: true }));
        } catch (err) {
            console.error('Failed to capture ILS screenshot:', err);
        }
    }, [setCapturedIls]);

    const [preview3D, setPreview3D] = useState(null); // { icao, runway, category } | null

    const handleDownloadKmz = useCallback(async (icao, runway, category, mode) => {
        try {
            const API_BASE = import.meta.env.VITE_API_URL || '/api';
            const url = `${API_BASE}/pans/ils-surfaces/${icao}_${runway}_${category}/kml?mode=${mode}${project?.id ? `&project_id=${project.id}` : ''}`;
            
            const response = await fetch(url);
            if (!response.ok) {
                const text = await response.text();
                throw new Error(`Failed to download KMZ: ${text}`);
            }
            
            const blob = await response.blob();
            const blobUrl = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = blobUrl;
            link.download = `${icao}_RWY_${runway}_CAT_${category}_OAS_${mode.toUpperCase()}.kmz`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(blobUrl);
        } catch (err) {
            console.error('KMZ Export error:', err);
            alert("Failed to export KMZ: " + err.message);
        }
    }, []);

    const currentMatchProc = getCurrentMatchProc();
    const currentResults = currentMatchProc?.results || [];

    // Mark the controlling obstacle — the worst (highest) penetration for this procedure
    if (currentResults.length > 0) {
        const worst = currentResults.reduce((a, b) => (b.penetration > a.penetration ? b : a));
        worst.isControlling = true;
    }

    const renderResultsContent = () => {
        if (!selectedProc) {
            return (
                <div style={{ padding: '20px 16px', textAlign: 'center', color: T.muted, fontSize: 12 }}>
                    Select an aerodrome, load ILS data, and click a procedure to view assessment details.
                </div>
            );
        }
        if (!ilsData[selectedProc.icao]) return null;
        const proc = ilsData[selectedProc.icao].procedures[selectedProc.procIdx];
        const aeroName = ilsResults[selectedProc.icao]?.aerodrome_name || validArps.find(a => a.icao === selectedProc.icao)?.name || '—';
        const rdhLabel = proc?.rdh_m != null ? `${Math.round(proc.rdh_m / 0.3048)}FT (${proc.rdh_m}m)` : '—';

        return (
            <div>
                <div style={{ padding: '10px 16px', borderBottom: `1px solid ${T.border}`, display: 'flex', alignItems: 'center', gap: 12 }}>
                    <div style={{ fontSize: 13, fontWeight: 800, color: T.blue }}>
                        {selectedProc.icao} – RWY {proc?.runway} ILS Results
                    </div>
                    <div style={{ flex: 1 }} />
                    {currentResults.length > 0 && (
                        <button
                            onClick={() => {
                                const headers = ["Obstacle", "Obstacle Elevation (m)", "V Tol (m)", "Total Elevation (m)", "Surface", "Surface Elevation (m)", "Surface Penetration (m)", "Comment", "Controlling"];
                                const rows = currentResults.map(r => [
                                    r.obstacle_name,
                                    r.obstacle_elevation?.toFixed(2),
                                    r.v_tolerance?.toFixed(2),
                                    r.total_elevation?.toFixed(2),
                                    r.surface_name,
                                    r.surface_elevation?.toFixed(2),
                                    r.penetration?.toFixed(2),
                                    r.comment,
                                    r.isControlling ? 'Yes' : ''
                                ]);
                                exportToCsv(headers, rows, `${selectedProc.icao}_RWY_${proc?.runway}_PANS_OPS_Assessment.csv`);
                            }}
                            style={{
                                padding: '3px 10px', fontSize: 10, fontWeight: 700,
                                background: T.blue, border: `1px solid ${T.blue}44`,
                                borderRadius: 5, color: T.white, cursor: 'pointer',
                                display: 'flex', alignItems: 'center', gap: 4, marginRight: 8
                            }}
                        >
                            📥 Export CSV
                        </button>
                    )}
                    <button
                        onClick={() => runIlsAssessment(selectedProc.icao)}
                        style={{
                            padding: '3px 10px', fontSize: 10, fontWeight: 700,
                            background: 'rgba(46,201,139,0.15)',
                            border: `1px solid ${T.safe}44`,
                            borderRadius: 5, color: T.safe, cursor: 'pointer',
                        }}
                    >
                        ▶ Assess
                    </button>
                </div>

                {/* Header info block — AD / ICAO DESIG / Threshold Elevation / RWY / GP / RDH */}
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11, marginTop: 10 }}>
                    <thead>
                        <tr style={{ background: T.bg0, borderBottom: `2px solid ${T.border}` }}>
                            {['AD', 'ICAO DESIG', 'Threshold Elevation (m)', 'RWY', 'GP', 'RDH'].map(h => (
                                <th key={h} style={{ padding: '8px 12px', textAlign: 'left', color: T.muted, fontWeight: 600 }}>{h}</th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        <tr style={{ borderBottom: `1px solid ${T.border}33` }}>
                            <td style={{ padding: '8px 12px', color: T.white }}>{aeroName}</td>
                            <td style={{ padding: '8px 12px', color: T.white, fontWeight: 700 }}>{selectedProc.icao}</td>
                            <td style={{ padding: '8px 12px', color: T.white }}>{currentMatchProc?.thr_elev_m ?? '—'}</td>
                            <td style={{ padding: '8px 12px', color: T.white, fontWeight: 700 }}>{proc?.runway}</td>
                            <td style={{ padding: '8px 12px', color: T.white }}>{proc?.gp_vpa_deg}°</td>
                            <td style={{ padding: '8px 12px', color: T.white }}>{rdhLabel}</td>
                        </tr>
                    </tbody>
                </table>

                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11, marginTop: 16 }}>
                    <thead>
                        <tr style={{ background: T.bg0, position: 'sticky', top: 0 }}>
                            {['Obstacle / Elevation (m)', 'V Tol (m)', 'Total Elevation (m)', 'Surface', 'Surface Elevation (m)', 'Surface Penetration (m)', 'Comment'].map(h => (
                                <th key={h} style={{ padding: '8px 12px', textAlign: 'left', color: T.muted, fontWeight: 600, borderBottom: `1px solid ${T.border}` }}>{h}</th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {currentResults.length > 0 ? currentResults.map((r, i) => (
                            <tr key={i} style={{ borderBottom: `1px solid ${T.border}22`, background: r.penetration > 0 ? 'rgba(232,64,64,0.08)' : 'transparent' }}>
                                <td style={{ padding: '8px 12px', color: T.white, fontWeight: 600 }}>
                                    {r.obstacle_name} ({r.obstacle_elevation?.toFixed(2)})
                                    {r.isControlling && <span style={{ marginLeft: 6, color: T.blue, fontSize: 9 }}>[Controlling]</span>}
                                </td>
                                <td style={{ padding: '8px 12px', color: T.white }}>{r.v_tolerance?.toFixed(2)}</td>
                                <td style={{ padding: '8px 12px', color: T.white, fontWeight: 700 }}>{r.total_elevation?.toFixed(2)}</td>
                                <td style={{ padding: '8px 12px', color: SURFACE_COLORS[r.surface_name] || T.white, fontWeight: 600 }}>{r.surface_name}</td>
                                <td style={{ padding: '8px 12px', color: T.white }}>{r.surface_elevation?.toFixed(2)}</td>
                                <td style={{ padding: '8px 12px', color: r.penetration > 0 ? T.danger : T.safe, fontWeight: 700 }}>
                                    {r.penetration?.toFixed(2)}
                                </td>
                                <td style={{ padding: '8px 12px' }}>
                                    <StatusBadge s={r.comment} />
                                </td>
                            </tr>
                        )) : (
                            <tr>
                                <td colSpan={7} style={{ padding: '20px 12px', textAlign: 'center', color: T.muted }}>
                                    {ilsResults[selectedProc.icao] ? 'No obstacles within ILS surfaces for this procedure.' : 'Load an aerodrome to run ILS assessment automatically.'}
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
        );
    };

    return (
        <div style={{ display: 'flex', height: '100%', overflow: 'hidden' }}>
            {/* Left panel */}
            <div style={{ width: 300, flexShrink: 0, background: T.bg1, backdropFilter: 'blur(var(--blur-md))', WebkitBackdropFilter: 'blur(var(--blur-md))', borderRight: `1px solid ${T.border}`, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                <div style={{ padding: '10px 14px', borderBottom: `1px solid ${T.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div style={{ fontSize: 12, fontWeight: 800, color: T.white }}>🛫 ILS Aerodromes ({validArps.length})</div>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: 11, color: T.muted }}>
                        <input type="checkbox" checked={validArps.length > 0 && validArps.every(a => visible[a.icao] !== false)} onChange={(e) => {
                            const val = e.target.checked;
                            const nextVis = { ...visible };
                            validArps.forEach(a => { nextVis[a.icao] = val; });
                            setVisible(nextVis);
                        }} style={{ accentColor: T.blue, cursor: 'pointer' }} />
                        All
                    </label>
                </div>

                {obsWithCoords.length > 1 && (
                    <div style={{ padding: '8px 12px', borderBottom: `1px solid ${T.border}` }}>
                        <div style={{ fontSize: 10, color: T.muted, marginBottom: 4 }}>SELECTED OBSTACLE</div>
                        <select value={selectedObs?.id || ''} onChange={e => setSelObstId(Number(e.target.value))}
                            style={{ width: '100%', background: T.bg0, border: `1px solid ${T.border}`, borderRadius: 6, padding: '5px 8px', color: T.white, fontSize: 11 }}>
                            {obsWithCoords.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
                        </select>
                    </div>
                )}

                <div style={{ flex: 1, overflowY: 'auto', paddingBottom: 8 }}>
                    {validArps.length === 0 && <div style={{ padding: 16, fontSize: 12, color: T.muted }}>No ILS aerodromes found.</div>}
                    {validArps.map((arp, i) => {
                        const isOn = visible[arp.icao] !== false;
                        const ad = ilsData[arp.icao];
                        const isLoading = loadingIls[arp.icao];
                        return (
                            <div key={i} style={{ borderBottom: `1px solid ${T.border}22` }}>
                                <div style={{ padding: '8px 14px', display: 'flex', alignItems: 'center', gap: 10 }}>
                                    <input type="checkbox" checked={isOn} onChange={() => {
                                        const nextOn = !isOn;
                                        setVisible(v => ({ ...v, [arp.icao]: nextOn }));
                                        // Also apply to all sub-checkboxes for this ICAO if loaded
                                        if (ad && ad.procedures) {
                                            const newSubVis = { ...visIls };
                                            ad.procedures.forEach(p => {
                                                const pk = `${arp.icao}_${p.runway}_${p.ils_category}`;
                                                newSubVis[pk] = nextOn;
                                            });
                                            setVisIls(newSubVis);
                                        }
                                    }} style={{ accentColor: T.blue, cursor: 'pointer' }} />
                                    <div style={{ flex: 1 }}>
                                        <div style={{ fontSize: 12, fontWeight: 700, color: isOn ? T.blue : T.muted }}>{arp.icao}</div>
                                        <div style={{ fontSize: 10, color: isOn ? T.muted : '#444' }}>{arp.name} · {arp.distance_km} km</div>
                                    </div>
                                    <button
                                        onClick={() => loadIlsData(arp.icao)}
                                        disabled={!!ad || isLoading}
                                        style={{
                                            padding: '3px 8px', fontSize: 10, fontWeight: 700,
                                            background: ad ? 'rgba(46,201,139,0.15)' : 'rgba(58,138,196,0.2)',
                                            border: `1px solid ${ad ? T.safe : T.blue}44`,
                                            borderRadius: 5, color: ad ? T.safe : T.blue,
                                            cursor: ad ? 'default' : 'pointer',
                                        }}
                                    >
                                        {isLoading ? '⟳' : ad ? '✓ Loaded' : 'Load ILS'}
                                    </button>
                                </div>

                                {/* Show procedures if loaded */}
                                {ad && ad.procedures && (
                                    <div style={{ padding: '0 14px 8px 36px' }}>
                                        {ad.procedures.map((proc, pi) => {
                                            const isSelected = selectedProc?.icao === arp.icao && selectedProc?.procIdx === pi;
                                            const procKey = `${arp.icao}_${proc.runway}_${proc.ils_category}`;
                                            const isVis = visIls[procKey] !== false;
                                            const isCaptured = capturedIls[procKey];
                                            return (
                                                <div key={pi} style={{ display: 'flex', alignItems: 'center', marginBottom: 2 }}>
                                                    <input
                                                        type="checkbox"
                                                        checked={isVis}
                                                        onChange={(e) => {
                                                            const val = e.target.checked;
                                                            setVisIls(v => ({ ...v, [procKey]: val }));
                                                            if (val) {
                                                                // If a sub-checkbox is clicked ON, the master must also be ON
                                                                setVisible(v => ({ ...v, [arp.icao]: true }));
                                                            }
                                                        }}
                                                        style={{ accentColor: T.blue, cursor: 'pointer', marginRight: 8 }}
                                                    />
                                                    <div
                                                        onClick={() => {
                                                            setSelectedProc({ icao: arp.icao, procIdx: pi });
                                                            if (!ilsResults[arp.icao]) runIlsAssessment(arp.icao);
                                                        }}
                                                        style={{
                                                            flex: 1, padding: '4px 8px',
                                                            borderRadius: 4, cursor: 'pointer',
                                                            background: isSelected ? 'rgba(58,138,196,0.2)' : 'transparent',
                                                            border: isSelected ? `1px solid ${T.blue}44` : '1px solid transparent',
                                                            fontSize: 11, color: isSelected ? T.blue : T.muted,
                                                            fontWeight: isSelected ? 700 : 400,
                                                        }}
                                                    >
                                                        RWY {proc.runway} · CAT {proc.ils_category}
                                                        <span style={{ fontSize: 9, color: '#666', marginLeft: 6 }}>
                                                            GP {proc.gp_vpa_deg}°
                                                        </span>
                                                    </div>
                                                    <button
                                                        onClick={(e) => { e.stopPropagation(); captureIlsScreenshot(arp.icao, proc.runway, proc.ils_category); }}
                                                        title="Capture Map Screenshot for Report"
                                                        style={{
                                                            background: isCaptured ? 'rgba(46,201,139,0.15)' : 'rgba(255,255,255,0.05)',
                                                            border: `1px solid ${isCaptured ? T.safe : T.border}`,
                                                            borderRadius: 4, padding: '3px 6px',
                                                            cursor: 'pointer', marginLeft: 4,
                                                            color: isCaptured ? T.safe : T.muted,
                                                            fontSize: 12
                                                        }}
                                                    >
                                                        {isCaptured ? '✓ Captured' : 'Screenshot'}
                                                    </button>
                                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                                        <button
                                                            onClick={(e) => { e.stopPropagation(); handleDownloadKmz(arp.icao, proc.runway, proc.ils_category, '2d'); }}
                                                            title="Download 2D KMZ (Flat Footprints)"
                                                            style={{
                                                                background: 'rgba(255,255,255,0.05)', border: `1px solid ${T.border}`,
                                                                borderRadius: 4, padding: '1px 4px', cursor: 'pointer', marginLeft: 4,
                                                                color: T.muted, fontSize: 10
                                                            }}
                                                        >
                                                            KMZ (2D)
                                                        </button>
                                                        <button
                                                            onClick={(e) => { e.stopPropagation(); handleDownloadKmz(arp.icao, proc.runway, proc.ils_category, '3d'); }}
                                                            title="Download 3D KMZ (True Altitudes)"
                                                            style={{
                                                                background: 'rgba(58,138,196,0.15)', border: `1px solid ${T.blue}44`,
                                                                borderRadius: 4, padding: '1px 4px', cursor: 'pointer', marginLeft: 4,
                                                                color: T.blue, fontSize: 10
                                                            }}
                                                        >
                                                            KMZ (3D)
                                                        </button>
                                                        <button
                                                            onClick={(e) => { e.stopPropagation(); setPreview3D({ icao: arp.icao, runway: proc.runway, category: proc.ils_category }); }}
                                                            title="Preview the ILS OAS surfaces and project obstacles in 3D, right here"
                                                            style={{
                                                                background: 'rgba(46,204,113,0.15)', border: '1px solid #2ECC7144',
                                                                borderRadius: 4, padding: '1px 4px', cursor: 'pointer', marginLeft: 4,
                                                                color: '#2ECC71', fontSize: 10
                                                            }}
                                                        >
                                                            🌐 3D
                                                        </button>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* Right: map + table */}
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', position: 'relative' }}>
                <div style={{ position: 'absolute', top: 12, right: 12, zIndex: 1000, display: 'flex', gap: 6, background: 'rgba(6,14,26,0.85)', borderRadius: 8, padding: 6, border: `1px solid ${T.border}`, backdropFilter: 'blur(8px)' }}>
                    {Object.entries(TILE_LAYERS).map(([key, layer]) => (
                        <button key={key} onClick={() => setMapMode(key)} style={{ padding: '4px 10px', borderRadius: 5, border: 'none', cursor: 'pointer', background: mapMode === key ? T.blue : 'transparent', color: mapMode === key ? T.white : T.muted, fontSize: 11, fontWeight: mapMode === key ? 700 : 400 }}>{layer.label}</button>
                    ))}
                </div>
                <ShieldControl top={92} shieldOn={shieldOn} toggleShield={toggleShield} shieldData={shieldData} shieldLoading={shieldLoading} shieldError={shieldError} projectMaxTotalElev={projectMaxTotalElev} projectPenetratingMaxTotalElev={projectPenetratingMaxTotalElev} radiusKm={radiusKm} setRadiusKm={setRadiusKm} exportCsv={exportCsv} />
                <MapMeasureTool map={mapState} top={52} tableOffset={resultsCollapsed ? 40 : 245} />
                <MapCoordinateViewer map={mapState} />
                <div ref={mapRef} style={{ flex: 1 }} />

                {/* Results table */}
                <div style={{ height: resultsCollapsed ? 38 : resultsHeight, background: T.bg1, borderTop: `1px solid ${T.border}`, display: 'flex', flexDirection: 'column', transition: 'height 0.2s ease', overflow: 'hidden', flexShrink: 0 }}>
                        {!resultsCollapsed && <ResizeHandle onMouseDown={startResultsResize} />}
                    <div style={{ padding: '8px 20px', background: T.bg0, borderBottom: `1px solid ${T.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
                        <div style={{ fontSize: 13, fontWeight: 700, color: T.white, display: 'flex', alignItems: 'center', gap: 6 }}>
                            <span>🏔</span> ILS Results
                        </div>
                        <div style={{ display: 'flex', gap: 12 }}>
                            {!resultsPoppedOut && (
                                <button
                                    onClick={() => {
                                        setResultsPoppedOut(true);
                                        setResultsCollapsed(true);
                                    }}
                                    style={{
                                        background: 'none', border: 'none', color: T.blueLt, cursor: 'pointer', fontSize: 11,
                                        display: 'flex', alignItems: 'center', gap: 4, fontWeight: 600
                                    }}
                                    title="Pop out results to a movable window"
                                >
                                    🗖 Pop Out
                                </button>
                            )}
                            <button
                                onClick={() => setResultsCollapsed(!resultsCollapsed)}
                                style={{
                                    background: 'none', border: 'none', color: T.blueLt, cursor: 'pointer', fontSize: 11,
                                    fontWeight: 600
                                }}
                            >
                                {resultsCollapsed ? '▲ Expand' : '▼ Collapse'}
                            </button>
                        </div>
                    </div>
                    <div style={{ flex: 1, overflowY: 'auto' }}>
                        {resultsPoppedOut ? (
                            <div style={{ padding: 20, textAlign: 'center', color: T.muted, fontSize: 13 }}>
                                Results are popped out. 
                                <button 
                                    onClick={() => { setResultsPoppedOut(false); setResultsCollapsed(false); }} 
                                    style={{ background: 'none', border: 'none', color: T.blue, textDecoration: 'underline', cursor: 'pointer', marginLeft: 6, fontWeight: 600 }}
                                >
                                    Bring back
                                </button>
                            </div>
                        ) : renderResultsContent()}
                    </div>
                </div>
            </div>

            {resultsPoppedOut && (
                <DraggableModal
                    title={`${project?.name || ''} - ILS Results`}
                    onClose={() => setResultsPoppedOut(false)}
                    width={850}
                    height={500}
                >
                    {renderResultsContent()}
                </DraggableModal>
            )}

            {preview3D && (
                <Cesium3DPreviewModal
                    title={`${preview3D.icao} RWY ${preview3D.runway} CAT ${preview3D.category} — ILS OAS 3D Preview`}
                    kmlUrl={`${import.meta.env.VITE_API_URL || '/api'}/pans/ils-surfaces/${preview3D.icao}_${preview3D.runway}_${preview3D.category}/kml?mode=3d${project?.id ? `&project_id=${project.id}` : ''}`}
                    onClose={() => setPreview3D(null)}
                />
            )}
        </div>
    );
}